import { interfaceIds } from '@soundxyz/sound-protocol/interfaceIds';
import { minterModuleV2_1Abi } from '../../abi/minter-module-v2_1.mjs';
import { soundEditionV1_2Abi } from '../../abi/sound-edition-v1_2.mjs';
import { UnsupportedMinterError } from '../../errors.mjs';
import { isHandledMinterInterfaceId } from '../../types.mjs';
import { minterAbiMap } from '../../utils/constants.mjs';
import { validateAddress, exhaustiveGuard } from '../../utils/helpers.mjs';
import { LazyPromise } from '../../utils/promise.mjs';

async function mintSchedules({
  editionAddress,
  scheduleIds: scheduleIdsArg,
  timestamp = Math.floor(Date.now() / 1e3)
}) {
  const scheduleIds = scheduleIdsArg || await editionScheduleIds.call(this, {
    editionAddress
  });
  const mintSchedulesLists = await Promise.all(
    scheduleIds.map(
      ({ minterAddress, mintIds }) => mintInfosFromMinter.call(this, { editionAddress, minterAddress, mintIds })
    )
  );
  const schedules = mintSchedulesLists.flat().sort((a, b) => {
    const startTimeDiff = a.startTime - b.startTime;
    if (startTimeDiff !== 0)
      return startTimeDiff;
    if (a.mintType === "MerkleDrop" && b.mintType !== "MerkleDrop") {
      return -1;
    }
    if (b.mintType === "MerkleDrop" && a.mintType !== "MerkleDrop") {
      return 1;
    }
    return 0;
  });
  const activeSchedules = schedules.filter((mintSchedule) => {
    return mintSchedule.startTime <= timestamp && mintSchedule.endTime > timestamp && !isSchedulePaused(mintSchedule);
  });
  return {
    schedules,
    activeSchedules
  };
}
async function editionScheduleIds({
  editionAddress,
  fromBlock
}) {
  const minterAddresses = await editionRegisteredMinters.call(this, {
    editionAddress,
    fromBlock
  });
  return Promise.all(
    minterAddresses.map(async (minterAddress) => {
      return {
        minterAddress,
        mintIds: await editionMinterMintIds.call(this, {
          editionAddress,
          minterAddress,
          fromBlock
        })
      };
    })
  );
}
function isSchedulePaused(schedule) {
  return "mintPaused" in schedule && schedule.mintPaused;
}
async function editionRegisteredMinters({
  editionAddress,
  fromBlock
}) {
  const {
    expectClient,
    instance: { onUnhandledError }
  } = this;
  const client = await expectClient();
  validateAddress(editionAddress, {
    type: "SOUND_EDITION"
  });
  const minterRole = await client.readContract({
    abi: soundEditionV1_2Abi,
    address: editionAddress,
    functionName: "MINTER_ROLE"
  });
  const rolesUpdatedFilter = await client.createEventFilter({
    event: {
      anonymous: false,
      inputs: [
        { indexed: true, internalType: "address", name: "user", type: "address" },
        {
          indexed: true,
          internalType: "uint256",
          name: "roles",
          type: "uint256"
        }
      ],
      name: "RolesUpdated",
      type: "event"
    },
    fromBlock: fromBlock ?? "earliest",
    address: editionAddress,
    args: {
      roles: minterRole
    },
    strict: true
  });
  const roleEvents = await client.getFilterLogs({
    filter: rolesUpdatedFilter
  });
  const candidateMinters = Array.from(
    roleEvents.reduce((acc, event) => {
      if (event.args?.user)
        acc.add(event.args.user);
      return acc;
    }, /* @__PURE__ */ new Set())
  );
  const minters = await Promise.all(
    candidateMinters.map(async (minterAddress) => {
      try {
        const moduleInterfaceId = await client.readContract({
          abi: minterModuleV2_1Abi,
          address: minterAddress,
          functionName: "moduleInterfaceId"
        });
        return isHandledMinterInterfaceId(moduleInterfaceId) ? minterAddress : null;
      } catch (err) {
        onUnhandledError(err);
        return null;
      }
    })
  );
  const allMinters = minters.reduce((acc, minter) => {
    if (minter)
      acc.add(minter);
    return acc;
  }, /* @__PURE__ */ new Set());
  return Array.from(allMinters);
}
async function editionMinterMintIds({
  editionAddress,
  minterAddress,
  fromBlock
}) {
  const client = await this.expectClient();
  validateAddress(editionAddress, {
    type: "SOUND_EDITION"
  });
  validateAddress(minterAddress, {
    type: "MINTER"
  });
  const filter = await client.createEventFilter({
    address: minterAddress,
    event: {
      anonymous: false,
      inputs: [
        {
          indexed: true,
          internalType: "address",
          name: "edition",
          type: "address"
        },
        {
          indexed: true,
          internalType: "address",
          name: "creator",
          type: "address"
        },
        {
          indexed: false,
          internalType: "uint128",
          name: "mintId",
          type: "uint128"
        },
        {
          indexed: false,
          internalType: "uint32",
          name: "startTime",
          type: "uint32"
        },
        {
          indexed: false,
          internalType: "uint32",
          name: "endTime",
          type: "uint32"
        },
        {
          indexed: false,
          internalType: "uint16",
          name: "affiliateFeeBPS",
          type: "uint16"
        }
      ],
      name: "MintConfigCreated",
      type: "event"
    },
    fromBlock: fromBlock ?? "earliest",
    args: {
      edition: editionAddress
    },
    strict: true
  });
  const mintScheduleConfigEvents = await client.getFilterLogs({
    filter
  });
  return Array.from(
    mintScheduleConfigEvents.reduce((acc, event) => {
      if (event.args?.mintId)
        acc.add(event.args.mintId);
      return acc;
    }, /* @__PURE__ */ new Set())
  );
}
async function mintInfosFromMinter({
  editionAddress,
  minterAddress,
  mintIds
}) {
  const {
    expectClient,
    instance: { idempotentCachedCall }
  } = this;
  validateAddress(editionAddress, {
    type: "SOUND_EDITION"
  });
  validateAddress(minterAddress, {
    type: "MINTER"
  });
  const clientPromise = LazyPromise(() => expectClient());
  const interfaceId = await idempotentCachedCall(`minter-interface-id-${minterAddress}`, async () => {
    const client = await clientPromise;
    return client.readContract({
      abi: minterModuleV2_1Abi,
      address: minterAddress,
      functionName: "moduleInterfaceId"
    });
  });
  return Promise.all(
    mintIds.map(async (mintId) => {
      if (!isHandledMinterInterfaceId(interfaceId)) {
        throw new UnsupportedMinterError({ interfaceId });
      }
      const { readContract } = await clientPromise;
      switch (interfaceId) {
        case interfaceIds.IRangeEditionMinter:
        case interfaceIds.IRangeEditionMinterV2:
        case interfaceIds.IRangeEditionMinterV2_1: {
          const scheduleWithPlatformFee = interfaceId === interfaceIds.IRangeEditionMinterV2_1 ? await readContract({
            abi: minterAbiMap[interfaceId],
            address: minterAddress,
            args: [editionAddress, BigInt(mintId)],
            functionName: "mintInfo"
          }) : null;
          const scheduleInfo = scheduleWithPlatformFee != null ? scheduleWithPlatformFee : interfaceId === interfaceIds.IRangeEditionMinter ? await readContract({
            abi: minterAbiMap[interfaceId],
            address: minterAddress,
            args: [editionAddress, BigInt(mintId)],
            functionName: "mintInfo"
          }) : interfaceId === interfaceIds.IRangeEditionMinterV2 ? await readContract({
            abi: minterAbiMap[interfaceId],
            address: minterAddress,
            args: [editionAddress, BigInt(mintId)],
            functionName: "mintInfo"
          }) : null;
          if (!scheduleInfo) {
            throw new UnsupportedMinterError({
              interfaceId
            });
          }
          return {
            mintType: "RangeEdition",
            affiliateFeeBPS: scheduleInfo.affiliateFeeBPS,
            cutoffTime: scheduleInfo.cutoffTime,
            editionAddress,
            endTime: scheduleInfo.endTime,
            interfaceId,
            maxMintable: (unixTimestamp) => (unixTimestamp || Math.floor(Date.now() / 1e3)) < scheduleInfo.cutoffTime ? scheduleInfo.maxMintableUpper : scheduleInfo.maxMintableLower,
            maxMintableLower: scheduleInfo.maxMintableLower,
            maxMintablePerAccount: scheduleInfo.maxMintablePerAccount,
            maxMintableUpper: scheduleInfo.maxMintableUpper,
            minterAddress,
            mintId: BigInt(mintId),
            mintPaused: scheduleInfo.mintPaused,
            price: scheduleInfo.price,
            startTime: scheduleInfo.startTime,
            totalMinted: scheduleInfo.totalMinted,
            platformTransactionFee: scheduleWithPlatformFee?.platformPerTxFlatFee ?? 0n
          };
        }
        case interfaceIds.IMerkleDropMinter:
        case interfaceIds.IMerkleDropMinterV2:
        case interfaceIds.IMerkleDropMinterV2_1: {
          const scheduleWithPlatformFee = interfaceId === interfaceIds.IMerkleDropMinterV2_1 ? await readContract({
            abi: minterAbiMap[interfaceId],
            address: minterAddress,
            args: [editionAddress, BigInt(mintId)],
            functionName: "mintInfo"
          }) : null;
          const scheduleInfo = scheduleWithPlatformFee != null ? scheduleWithPlatformFee : interfaceId === interfaceIds.IMerkleDropMinter ? await readContract({
            abi: minterAbiMap[interfaceId],
            address: minterAddress,
            args: [editionAddress, BigInt(mintId)],
            functionName: "mintInfo"
          }) : interfaceId === interfaceIds.IMerkleDropMinterV2 ? await readContract({
            abi: minterAbiMap[interfaceId],
            address: minterAddress,
            args: [editionAddress, BigInt(mintId)],
            functionName: "mintInfo"
          }) : null;
          if (!scheduleInfo) {
            throw new UnsupportedMinterError({
              interfaceId
            });
          }
          return {
            mintType: "MerkleDrop",
            affiliateFeeBPS: scheduleInfo.affiliateFeeBPS,
            editionAddress,
            endTime: scheduleInfo.endTime,
            interfaceId,
            maxMintablePerAccount: scheduleInfo.maxMintablePerAccount,
            minterAddress,
            mintId: BigInt(mintId),
            mintPaused: scheduleInfo.mintPaused,
            price: scheduleInfo.price,
            startTime: scheduleInfo.startTime,
            totalMinted: scheduleInfo.totalMinted,
            maxMintable: scheduleInfo.maxMintable,
            merkleRoot: scheduleInfo.merkleRootHash,
            platformTransactionFee: scheduleWithPlatformFee?.platformPerTxFlatFee ?? 0n
          };
        }
        default: {
          exhaustiveGuard(interfaceId);
        }
      }
    })
  );
}

export { editionMinterMintIds, editionRegisteredMinters, editionScheduleIds, isSchedulePaused, mintInfosFromMinter, mintSchedules };
